
import React, { useState, useEffect } from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Mic, MicOff } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface ChatInputProps {
  onSendMessage: (message: string) => Promise<void>;
  placeholder?: string;
  isListening: boolean;
  isSending: boolean;
  isVoiceEnabled: boolean;
  isAuthenticated: boolean;
  startListening: () => void;
  stopListening: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  placeholder = "Type your message...",
  isListening,
  isSending,
  isVoiceEnabled,
  isAuthenticated,
  startListening,
  stopListening
}) => {
  const [inputValue, setInputValue] = useState('');
  
  // Update inputValue if we're listening and get a transcript
  useEffect(() => {
    if (isListening) {
      // Input will be handled by the speech recognition callback
    }
  }, [isListening]);
  
  const handleSend = async (e?: React.FormEvent) => {
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (inputValue.trim() === '' || isSending) return;
    
    try {
      await onSendMessage(inputValue);
      setInputValue('');
    } catch (error) {
      console.error("Error in ChatInput:", error);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSend();
    }
  };
  
  const handleVoiceButtonClick = () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to use voice features.",
        variant: "destructive"
      });
      return;
    }
    
    if (isListening) {
      console.log('Stopping listening');
      stopListening();
    } else {
      console.log('Starting listening');
      startListening();
    }
  };
  
  return (
    <div className="border-t p-4">
      <form onSubmit={handleSend} className="flex gap-2">
        <Textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isListening ? "Listening..." : placeholder}
          className="resize-none"
          rows={1}
        />
        
        <Button
          type="button"
          onClick={handleVoiceButtonClick}
          variant={isListening ? "destructive" : "outline"}
          size="icon"
          className="flex-shrink-0"
          disabled={!isVoiceEnabled}
        >
          {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        
        <Button 
          type="submit"
          disabled={inputValue.trim() === '' || isSending}
          size="icon"
          className="flex-shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};

export default ChatInput;
